/* R148 · Feb 2026 — HR Admin Records tab
 * Admin/HR view for ALL members' Attendance / Leave / WFH records with
 * filters, inline approve/reject, and per-record comment thread. */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, MessageSquare, Filter, RefreshCw, Download, Upload, FileText } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const KINDS = [
  { key: "attendance", label: "Attendance", singular: "attendance" },
  { key: "leave", label: "Leaves", singular: "leave" },
  { key: "wfh", label: "WFH", singular: "wfh" },
];

const STATUS_OPTIONS = {
  attendance: ["", "present", "half_day", "short", "leave", "half_day_leave", "wfh", "absent"],
  leave: ["", "pending", "approved", "rejected", "cancelled"],
  wfh: ["", "pending", "approved", "rejected"],
};

export default function HrAdminRecordsTab({ wsId }) {
  const [tab, setTab] = useState("attendance");
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
  }, [wsId]);

  return (
    <div className="space-y-4" data-testid="hr-admin-records">
      <div className="border border-primary/20 bg-primary/5 p-3 text-xs">
        <span className="label-eyebrow text-[10px] text-primary">HR Admin</span>
        <span className="ml-2">Full view of every teammate&apos;s records. Approve, reject and add comments inline. Only visible to HR (<code className="font-mono text-[11px]">hr.manage</code> / <code className="font-mono text-[11px]">hr.approve</code>).</span>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none">
          {KINDS.map((k) => (
            <TabsTrigger key={k.key} value={k.key} className="rounded-none" data-testid={`hra-tab-${k.key}`}>
              {k.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {KINDS.map((k) => (
          <TabsContent key={k.key} value={k.key} className="mt-4">
            <RecordsTable wsId={wsId} kind={k.key} singular={k.singular} members={members} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function RecordsTable({ wsId, kind, singular, members }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 7) + "-01";
  const [filters, setFilters] = useState({ from: firstOfMonth, to: today, user_id: "_all", status: "" });
  const [commentsFor, setCommentsFor] = useState(null); // record row or null

  const load = async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      if (filters.user_id && filters.user_id !== "_all") params.set("user_id", filters.user_id);
      if (filters.status) params.set("status", filters.status);
      const { data } = await api.get(`/workspaces/${wsId}/hr-admin/${kind === "leave" ? "leaves" : kind}?${params.toString()}`);
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load records"));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, kind, filters.from, filters.to, filters.user_id, filters.status]);

  const review = async (row, approve) => {
    const path = kind === "leave"
      ? `/leaves/${row.id}/review`
      : kind === "wfh"
        ? `/wfh/${row.id}/review`
        : null;
    if (!path) return;
    const reason = approve ? null : (window.prompt("Reason for rejecting?") || "");
    try {
      await api.post(path, { approve, reason });
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const statuses = STATUS_OPTIONS[kind] || [""];

  return (
    <div className="space-y-3" data-testid={`hra-table-${kind}`}>
      <div className="flex flex-wrap items-end gap-2 border border-border bg-muted/20 p-3">
        <div className="flex items-center gap-1 mr-2 text-xs text-muted-foreground"><Filter className="w-3 h-3" />Filters</div>
        <div>
          <div className="label-eyebrow text-[9px] mb-1">From</div>
          <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="h-8 text-xs w-36" data-testid={`hra-${kind}-from`} />
        </div>
        <div>
          <div className="label-eyebrow text-[9px] mb-1">To</div>
          <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="h-8 text-xs w-36" data-testid={`hra-${kind}-to`} />
        </div>
        <div>
          <div className="label-eyebrow text-[9px] mb-1">Member</div>
          <Select value={filters.user_id} onValueChange={(v) => setFilters({ ...filters, user_id: v })}>
            <SelectTrigger className="h-8 text-xs w-52" data-testid={`hra-${kind}-user`}><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All members</SelectItem>
              {members.filter((m) => m.status === "active").map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email || m.user_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-eyebrow text-[9px] mb-1">Status</div>
          <Select value={filters.status || "_any"} onValueChange={(v) => setFilters({ ...filters, status: v === "_any" ? "" : v })}>
            <SelectTrigger className="h-8 text-xs w-40" data-testid={`hra-${kind}-status`}><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">Any status</SelectItem>
              {statuses.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={load} disabled={busy} data-testid={`hra-${kind}-refresh`}>
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
        {(kind === "leave" || kind === "wfh") && (
          <ExportImportButtons wsId={wsId} kind={kind} filters={filters} onImported={load} />
        )}
        {kind === "leave" && <AdjustLeaveButton wsId={wsId} />}
        <span className="ml-auto text-xs text-muted-foreground" data-testid={`hra-${kind}-count`}>{rows.length} rows</span>
      </div>

      {busy && rows.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid={`hra-${kind}-empty`}>
          No records match these filters.
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/40 border-b border-border">
                <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Member</th>
                {kind === "attendance" && <>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Date</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Clock in</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Clock out</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Work min</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]" title="WiFi network the user's device was connected to when they clocked in. Multiple punches join with · ">WiFi</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Status</th>
                </>}
                {kind === "leave" && <>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Type</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">From</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">To</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Days</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Reason</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Status</th>
                </>}
                {kind === "wfh" && <>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Date</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Reason</th>
                  <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Status</th>
                </>}
                <th className="text-right px-3 py-2 label-eyebrow text-[9px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border hover:bg-muted/20" data-testid={`hra-${kind}-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium truncate">{r.user_name || r.user_id}</div>
                    <div className="text-[10px] text-muted-foreground">{r.user_email}</div>
                  </td>
                  {kind === "attendance" && <>
                    <td className="px-3 py-2 font-mono">{r.date}</td>
                    <td className="px-3 py-2 font-mono">{fmtTime(r.clock_in)}</td>
                    <td className="px-3 py-2 font-mono">{fmtTime(r.clock_out)}</td>
                    <td className="px-3 py-2 font-mono">{r.work_minutes || 0}</td>
                    <td className="px-3 py-2 text-[11px]" title={r.wifi_ssid || "No WiFi captured"} data-testid={`hra-wifi-${r.id}`}>
                      {r.wifi_ssid
                        ? <span className="inline-block max-w-[16ch] truncate border border-border bg-muted/30 px-1.5">{r.wifi_ssid}</span>
                        : <span className="text-muted-foreground/60">—</span>}
                    </td>
                    <td className="px-3 py-2"><StatusChip s={r.status} /></td>
                  </>}
                  {kind === "leave" && <>
                    <td className="px-3 py-2"><Badge variant="outline" className="rounded-none text-[9px]">{r.type_code}</Badge></td>
                    <td className="px-3 py-2 font-mono">{r.from_date}</td>
                    <td className="px-3 py-2 font-mono">{r.to_date}</td>
                    <td className="px-3 py-2 font-mono">{r.days}</td>
                    <td className="px-3 py-2 max-w-[240px] truncate" title={r.reason}>{r.reason}</td>
                    <td className="px-3 py-2"><StatusChip s={r.status} /></td>
                  </>}
                  {kind === "wfh" && <>
                    <td className="px-3 py-2 font-mono">{r.date}</td>
                    <td className="px-3 py-2 max-w-[240px] truncate" title={r.reason}>{r.reason}</td>
                    <td className="px-3 py-2"><StatusChip s={r.status} /></td>
                  </>}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="rounded-none h-6 px-1.5 text-xs" onClick={() => setCommentsFor(r)} data-testid={`hra-${kind}-comments-${r.id}`}>
                        <MessageSquare className="w-3 h-3 mr-1" />{r.comment_count || 0}
                      </Button>
                      {(kind === "leave" || kind === "wfh") && r.status === "pending" && (
                        <>
                          <Button size="sm" variant="ghost" className="rounded-none h-6 px-1 text-emerald-600" onClick={() => review(r, true)} data-testid={`hra-${kind}-approve-${r.id}`}>
                            <CheckCircle2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-none h-6 px-1 text-red-600" onClick={() => review(r, false)} data-testid={`hra-${kind}-reject-${r.id}`}>
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {commentsFor && (
        <CommentsDialog
          kind={kind}
          record={commentsFor}
          onClose={() => setCommentsFor(null)}
          onCountChange={() => load()}
        />
      )}
    </div>
  );
}

function StatusChip({ s }) {
  const cls = {
    pending: "bg-amber-500/10 text-amber-700 border-amber-500/40",
    approved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
    rejected: "bg-red-500/10 text-red-700 border-red-500/40",
    cancelled: "bg-gray-500/10 text-gray-700 border-gray-500/40",
    present: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
    half_day: "bg-amber-500/10 text-amber-700 border-amber-500/40",
    short: "bg-orange-500/10 text-orange-700 border-orange-500/40",
    leave: "bg-sky-500/10 text-sky-700 border-sky-500/40",
    half_day_leave: "bg-sky-500/10 text-sky-700 border-sky-500/40",
    wfh: "bg-violet-500/10 text-violet-700 border-violet-500/40",
    absent: "bg-red-500/10 text-red-700 border-red-500/40",
  }[s] || "bg-muted text-muted-foreground border-border";
  return <span className={`inline-block px-1.5 py-0.5 border text-[10px] rounded-none ${cls}`}>{s || "—"}</span>;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

function CommentsDialog({ kind, record, onClose, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(true);
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/hr-records/${kind}/${record.id}/comments`);
      setComments(data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load comments"));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [record.id]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/hr-records/${kind}/${record.id}/comments`, { text: text.trim() });
      setText("");
      await load();
      onCountChange && onCountChange();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to post"));
    } finally { setPosting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid={`hra-comments-dialog-${kind}-${record.id}`}>
        <DialogHeader>
          <DialogTitle>Comments · {record.user_name || record.user_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground border border-border bg-muted/20 p-2">
            {kind === "attendance" && <>Attendance record for <b>{record.date}</b> · status <StatusChip s={record.status} /></>}
            {kind === "leave" && <>Leave request <b>{record.from_date} → {record.to_date}</b> · status <StatusChip s={record.status} /></>}
            {kind === "wfh" && <>WFH request for <b>{record.date}</b> · status <StatusChip s={record.status} /></>}
          </div>
          <div className="max-h-[40vh] overflow-y-auto border border-border">
            {busy && comments.length === 0 ? (
              <div className="text-sm text-muted-foreground p-4 text-center">Loading…</div>
            ) : comments.length === 0 ? (
              <div className="text-sm text-muted-foreground p-4 text-center" data-testid="hra-comments-empty">
                No comments yet. Be the first to add context, ask a question, or explain a decision.
              </div>
            ) : (
              comments.map((c) => (
                <div key={c.id} className="border-b border-border last:border-b-0 px-3 py-2" data-testid={`hra-comment-${c.id}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold">{c.author_name || c.author_email}</span>
                    <span className="text-muted-foreground text-[10px]">{fmtRelative(c.created_at)}</span>
                  </div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{c.text}</div>
                </div>
              ))
            )}
          </div>
          <div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a comment (visible to HR + the record owner)…"
              className="min-h-[80px]"
              data-testid="hra-comment-input"
            />
            <div className="flex justify-end mt-2">
              <Button className="rounded-none" onClick={post} disabled={posting || !text.trim()} data-testid="hra-comment-submit">
                {posting ? "Posting…" : "Post comment"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// Feb 2026 · CSV export + import buttons for HR Admin Leaves and WFH tabs.
// Export streams the current-filter rows straight from the server; Import
// parses a CSV client-side and POSTs rows in a single batch.
function ExportImportButtons({ wsId, kind, filters, onImported }) {
  const [importOpen, setImportOpen] = useState(false);
  const label = kind === "leave" ? "leaves" : "wfh";
  const doExport = () => {
    const q = new URLSearchParams();
    if (filters.from) q.set("from", filters.from);
    if (filters.to) q.set("to", filters.to);
    const path = `/api/v1/workspaces/${wsId}/hr-admin/${label}/export?${q.toString()}`;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    // Server enforces auth via cookie/header; use fetch → blob → download.
    fetch(`${base}${path}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("co_token")}` },
    })
      .then((r) => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${label}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${kind === "leave" ? "Leaves" : "WFH"} CSV downloaded`);
      })
      .catch((e) => toast.error(apiErrorMessage(e, "Export failed")));
  };
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={doExport} data-testid={`hra-${kind}-export`}>
        <Download className="w-3 h-3 mr-1" />Export
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setImportOpen(true)} data-testid={`hra-${kind}-import`}>
        <Upload className="w-3 h-3 mr-1" />Import
      </Button>
      {importOpen && (
        <ImportDialog wsId={wsId} kind={kind}
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); onImported && onImported(); }} />
      )}
    </>
  );
}

function ImportDialog({ wsId, kind, onClose, onDone }) {
  const label = kind === "leave" ? "leaves" : "wfh";
  const template = kind === "leave"
    ? "user_email,type_code,from_date,to_date,half_day,reason,status\nalice@acme.com,CL,2025-06-10,2025-06-11,false,Family event,approved"
    : "user_email,date,reason,status\nalice@acme.com,2025-06-15,Plumber,approved";
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${label}-import-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Simple CSV parser handling quoted values + commas inside quotes.
  const parseCsv = (raw) => {
    const rows = [];
    const lines = raw.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return { rows: [] };
    const parseLine = (l) => {
      const out = []; let cur = ""; let q = false;
      for (const ch of l) {
        if (ch === '"') { q = !q; continue; }
        if (ch === "," && !q) { out.push(cur); cur = ""; continue; }
        cur += ch;
      }
      out.push(cur);
      return out.map((v) => v.trim());
    };
    const headers = parseLine(lines[0]);
    for (let i = 1; i < lines.length; i++) {
      const cells = parseLine(lines[i]);
      const row = {};
      headers.forEach((h, idx) => { row[h] = cells[idx] ?? ""; });
      // Coerce known fields.
      if ("half_day" in row) row.half_day = String(row.half_day).toLowerCase() === "true";
      rows.push(row);
    }
    return { rows };
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const t = await f.text();
    setText(t);
  };

  const doImport = async () => {
    const { rows } = parseCsv(text);
    if (rows.length === 0) { toast.error("CSV must have at least one data row"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/hr-admin/${label}/import`,
        { rows, [kind === "leave" ? "skip_overlapping" : "skip_duplicates"]: true });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} row${data.created === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      } else {
        toast.error(`All ${data.skipped} row${data.skipped === 1 ? "" : "s"} skipped — review errors below`);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`import-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>Import {kind === "leave" ? "leave" : "WFH"} records</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border p-3 text-xs bg-muted/20">
            <div className="font-semibold mb-1">CSV format · header row required</div>
            <code className="block font-mono text-[11px] whitespace-pre-wrap">{template}</code>
            <div className="flex items-center justify-between mt-2">
              <span className="text-muted-foreground text-[10px]">
                {kind === "leave" ? "Overlapping requests are skipped." : "Duplicate (user, date) rows are skipped."}
                {" "}Unknown emails / types are skipped.
              </span>
              <Button size="sm" variant="ghost" onClick={downloadTemplate} className="rounded-none h-7 text-xs" data-testid={`import-${kind}-template`}>
                <FileText className="w-3 h-3 mr-1" />Template
              </Button>
            </div>
          </div>
          <div>
            <div className="text-xs mb-1">Upload CSV or paste rows:</div>
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-xs mb-2" data-testid={`import-${kind}-file`} />
            <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)}
              placeholder={template}
              className="font-mono text-[11px] rounded-none"
              data-testid={`import-${kind}-textarea`} />
          </div>
          {result && (
            <div className="border border-border p-3 text-xs" data-testid={`import-${kind}-result`}>
              <div><strong className="text-emerald-700">Created: {result.created}</strong> · Skipped: <strong>{result.skipped}</strong></div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto border border-border">
                  {result.errors.map((er, i) => (
                    <div key={i} className="px-2 py-1 border-b border-border text-[11px]">
                      <span className="font-mono text-muted-foreground">Row {er.row}:</span> {er.reason}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {result?.created > 0 ? (
            <Button onClick={onDone} data-testid={`import-${kind}-done`}>Done · refresh</Button>
          ) : (
            <Button onClick={doImport} disabled={busy || !text.trim()} data-testid={`import-${kind}-run`}>
              {busy ? "Importing…" : "Run import"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 · Manual leave balance adjustment (credit/debit) with audit
// trail. Reason is required; delta_days can be negative (debit) for
// unpaid conversions or year-end write-offs.
function AdjustLeaveButton({ wsId }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [userId, setUserId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [uRes, tRes, hRes] = await Promise.all([
          api.get(`/workspaces/${wsId}/members`),
          api.get(`/workspaces/${wsId}/leave-types`),
          api.get(`/workspaces/${wsId}/hr-admin/leaves/adjustments`, { params: { limit: 20 } }),
        ]);
        setUsers(uRes.data?.members || uRes.data || []);
        setTypes(tRes.data?.types || tRes.data || []);
        setHistory(hRes.data?.adjustments || []);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    })();
  }, [open, wsId]);

  const save = async () => {
    const d = Number(delta);
    if (!userId || !typeId || !reason.trim() || !Number.isFinite(d) || d === 0) {
      toast.error("Employee, leave type, non-zero days, and reason are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/hr-admin/leaves/adjust`, {
        user_id: userId, leave_type_id: typeId, delta_days: d,
        reason: reason.trim(), effective_date: effective,
      });
      toast.success(`${d > 0 ? "Credited" : "Debited"} ${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"}`);
      setHistory((h) => [data, ...h]);
      setDelta(""); setReason("");
    } catch (e) { toast.error(apiErrorMessage(e, "Adjustment failed")); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setOpen(true)} data-testid="hra-leave-adjust-open">
        <Filter className="w-3 h-3 mr-1" /> Adjust balance
      </Button>
      {open && (
        <Dialog open onOpenChange={(v) => { if (!v) setOpen(false); }}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="adjust-leave-dialog">
            <DialogHeader>
              <DialogTitle>Adjust leave balance</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Employee</div>
                  <Select value={userId} onValueChange={setUserId}>
                    <SelectTrigger className="h-9 text-xs rounded-none" data-testid="adjust-user"><SelectValue placeholder="Pick employee…" /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.user_id || u.id} value={u.user_id || u.id}>{u.name || u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Leave type</div>
                  <Select value={typeId} onValueChange={setTypeId}>
                    <SelectTrigger className="h-9 text-xs rounded-none" data-testid="adjust-type"><SelectValue placeholder="Pick type…" /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} · {t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Δ Days (+ credit / − debit)</div>
                  <Input type="number" step="0.5" value={delta} onChange={(e) => setDelta(e.target.value)}
                    placeholder="e.g. 5 or -2" className="h-9 rounded-none" data-testid="adjust-delta" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Effective date</div>
                  <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)}
                    className="h-9 rounded-none" data-testid="adjust-effective" />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Reason (audit trail)</div>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. FY26 carry-forward · Unpaid leave adjustment · Comp-off grant"
                  className="text-xs rounded-none" data-testid="adjust-reason" />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Close</Button>
                <Button size="sm" onClick={save} disabled={busy} data-testid="adjust-save">
                  {busy ? "Saving…" : "Save adjustment"}
                </Button>
              </div>
              {history.length > 0 && (
                <div className="border border-border">
                  <div className="px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Recent adjustments</div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-border">
                    {history.map((h) => (
                      <div key={h.id} className="px-3 py-1.5 flex items-center gap-2 text-[11px]" data-testid={`adjust-history-${h.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{h.user_name || h.user_email}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{h.leave_type_code} · {h.effective_date} · {h.reason}</div>
                        </div>
                        <div className={`font-mono font-bold ${h.delta_days > 0 ? "text-emerald-700" : "text-red-700"}`}>
                          {h.delta_days > 0 ? "+" : ""}{h.delta_days}d
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

